import * as React from 'react'
import { PropsWithChildren, useContext } from 'react'

import { isInBrowser } from '../utilities/browser'
import { getErrorMessage, getSafeErrorMessage, hasValue } from '../utilities/data'
import { fetchLocationData } from '../async/civic-info'
import { LocationResponse } from '../types/civic-info-types'
import { BallotReadyRepresentativeMap } from '../types/ballot-ready-types'
import { fetchBallotReadyRepresentatives } from '../async/ballot-ready'
import { authState } from '../state/auth-state'
import { UserLocation } from '../types/account-types'
import { getHomeLocation, isSavedNonHomeAddress } from '../utilities/locations'

export const REPRESENTATIVES_HOME_ONLY_MESSAGE = 'Representatives are shown for My Location only.'

type LocationSettings = {
  address?: string
  prioritizeLocation?: boolean
  restrictToLocation?: boolean
}
export const LocationContext = React.createContext<{
  settings: LocationSettings
  locationData?: LocationResponse
  locationLoading?: boolean
  locationError?: string
  representativesData?: BallotReadyRepresentativeMap
  representativesLoading?: boolean
  representativesError?: string
  // The signed-in user's home, and whether the current address is one of their
  // other saved locations (representatives are home-only).
  homeLocation?: UserLocation
  addressIsSavedNonHome?: boolean
  setSettings: (settings: LocationSettings) => void
  setLocationData: (locationData?: LocationResponse) => void
}>({
  settings: {},
  setSettings: () => {},
  setLocationData: () => {}
})

const getLocationFromLocalStorage = () => {
  const location = localStorage.getItem('INI_AT_LOCATION_DATA')
  if (location) {
    return JSON.parse(location)
  }
}

export const LocationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { account } = authState()
  const homeLocation = getHomeLocation(account)
  const [settings, updateSettings] = React.useState<LocationSettings>({
    address: (isInBrowser() && localStorage.getItem('INI_AT_ADDRESS')) || undefined,
    prioritizeLocation:
      (isInBrowser() && Boolean(localStorage.getItem('INI_AT__USER_PRIORITIZE_LOCATION'))) ||
      undefined,
    restrictToLocation:
      (isInBrowser() && Boolean(localStorage.getItem('INI_AT__USER_RESTRICT_TO_LOCATION'))) ||
      undefined
  })

  const [locationError, setLocationError] = React.useState<string>()
  const [locationLoading, islocationLoading] = React.useState(false)
  const [locationData, setLocationData] = React.useState<LocationResponse | undefined>(
    (isInBrowser() && getLocationFromLocalStorage()) || undefined
  )
  const [representativesError, setRepresentativesError] = React.useState<string>()
  const [representativesLoading, isRepresentativesLoading] = React.useState(false)
  const [representativesData, setRepresentativesData] = React.useState<
    BallotReadyRepresentativeMap | undefined
  >(undefined)

  const fetchRepresentatives = React.useCallback(async () => {
    try {
      if (isSavedNonHomeAddress(account, settings.address)) {
        // Representatives are looked up for the home only; don't show another
        // saved location's as if they were the user's.
        setRepresentativesData(undefined)
        setRepresentativesError(REPRESENTATIVES_HOME_ONLY_MESSAGE)
      } else if (hasValue(settings.address)) {
        setRepresentativesData(undefined)
        setRepresentativesError(undefined)
        isRepresentativesLoading(true)
        const fetchedMap = await fetchBallotReadyRepresentatives(settings.address)

        if (fetchedMap) {
          setRepresentativesData(fetchedMap)
        } else {
          setRepresentativesData(undefined)
        }
      }
    } catch (err) {
      setRepresentativesError(
        getSafeErrorMessage(
          err,
          "We couldn't look up representatives for that address. Please enter a full street address (including city, state and zip code) and try again."
        )
      )
    } finally {
      isRepresentativesLoading(false)
    }
  }, [settings.address, account])

  React.useEffect(() => {
    fetchRepresentatives()
  }, [settings.address])

  const updateLocation = async (addressUpdate: string, persist = true) => {
    islocationLoading(true)
    setLocationError(undefined)
    try {
      const data = await fetchLocationData(addressUpdate)

      if (persist) {
        localStorage.setItem('INI_AT_LOCATION_DATA', JSON.stringify(data))
      }
      setLocationData(data)
    } catch (err) {
      setLocationError(
        getErrorMessage(
          err,
          "We couldn't find your location info from the provided address. Please try again."
        )
      )
    }
    islocationLoading(false)
  }
  // With no address chosen, default to the signed-in user's home for this
  // visit. It isn't written to localStorage, which outlives sign-out (on a
  // shared device the next person would see it).
  const homeLocationText = homeLocation?.locationText
  React.useEffect(() => {
    if (homeLocationText && !settings.address) {
      updateSettings(current => ({ ...current, address: homeLocationText }))
      updateLocation(homeLocationText, false)
    }
  }, [homeLocationText])

  const setSettings = async ({
    address: newAddress,
    prioritizeLocation: newPrioritizeLocation,
    restrictToLocation: newRestricToLocation
  }: typeof settings) => {
    isInBrowser() && newAddress && newAddress !== ''
      ? localStorage.setItem('INI_AT_ADDRESS', newAddress)
      : localStorage.removeItem('INI_AT_ADDRESS')

    const addressUpdate = !newAddress || newAddress === '' ? undefined : newAddress
    if (!addressUpdate) {
      setLocationData(undefined)
      setRepresentativesData(undefined)
    } else if (addressUpdate !== settings.address) {
      updateLocation(addressUpdate)
    }

    updateSettings({
      address: addressUpdate,
      prioritizeLocation:
        newPrioritizeLocation ||
        // auto set this to true if user is going from no address to an address
        Boolean(!settings.address && addressUpdate),
      restrictToLocation: newRestricToLocation
    })
  }

  return (
    <LocationContext.Provider
      value={{
        settings,
        setSettings,
        locationData,
        setLocationData,
        locationLoading,
        locationError,
        representativesData,
        representativesLoading,
        representativesError,
        homeLocation,
        addressIsSavedNonHome: isSavedNonHomeAddress(account, settings.address)
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}

export const locationState = () => useContext(LocationContext)
